import { useState, useEffect, useCallback } from 'react';
import {
  Table, Title, Badge, Button, Group, Select, Text,
  Stack, Alert, Loader, Center, Modal, ActionIcon, Tooltip, CopyButton,
  TextInput,
} from '@mantine/core';
import { IconAlertCircle, IconPlus, IconTrash, IconCopy, IconCheck, IconCertificate, IconUserPlus } from '@tabler/icons-react';
import { api } from '../../apiClient';
import UserCertificationStatus from './UserCertificationStatus';

/**
 * ユーザー管理パネル
 * - 全ユーザー（メールアドレス付き）を一覧表示する
 * - user_roles テーブルを通じてユーザーへのロール付与・剥奪を管理する
 * - ユーザーID・メールアドレスのクリップボードコピーに対応する
 */
function UserManagement() {
  const [users, setUsers] = useState([]);
  const [userRoles, setUserRoles] = useState({});
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [certStatusUser, setCertStatusUser] = useState(null);

  // 追加モーダルのフォーム状態（ロール付与）
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [newRoleId, setNewRoleId] = useState(null);
  const [saving, setSaving] = useState(false);

  // ユーザー追加モーダルの状態
  const [addUserModalOpen, setAddUserModalOpen] = useState(false);
  const [addUserEmail, setAddUserEmail] = useState('');
  const [addUserEmailError, setAddUserEmailError] = useState(null);
  const [addUserLoading, setAddUserLoading] = useState(false);
  const [addedUser, setAddedUser] = useState(null);

  // ユーザー一覧・ロール情報を並列取得する
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [usersData, userRolesData, rolesData] = await Promise.all([
        api.get('/admin/users'),
        api.get('/user-roles', { all: 'true' }),
        api.get('/roles'),
      ]);

      // ロールIDをキーにしたマップを作成する
      const roleMap = {};
      for (const r of rolesData ?? []) {
        roleMap[r.id] = r;
      }

      // user_id ごとにロールをまとめたマップを作成する
      const rolesByUser = {};
      for (const row of userRolesData ?? []) {
        if (!rolesByUser[row.user_id]) rolesByUser[row.user_id] = [];
        const role = roleMap[row.role_id];
        if (role) rolesByUser[row.user_id].push(role);
      }

      const sortedUsers = (usersData ?? []).sort((a, b) =>
        (a.email ?? '').localeCompare(b.email ?? '')
      );

      setUsers(sortedUsers);
      setUserRoles(rolesByUser);
      setRoles(rolesData ?? []);
    } catch (err) {
      console.error('ユーザー管理データの取得に失敗しました:', err);
      setError('データの取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ロールを剥奪する
  const handleRemoveRole = async (userId, roleId) => {
    try {
      await api.delete('/user-roles', { userId, roleId });
      await fetchData();
    } catch (e) {
      console.error('ロールの削除に失敗しました:', e);
      setError('ロールの削除に失敗しました。');
    }
  };

  // ロールを付与する
  const handleAddRole = async () => {
    if (!selectedUserId || !newRoleId) return;
    setSaving(true);
    setError(null);

    try {
      await api.post('/user-roles', { user_id: selectedUserId, role_id: newRoleId });
    } catch (e) {
      console.error('ロールの付与に失敗しました:', e);
      setError('ロールの付与に失敗しました。既に付与済みか、データを確認してください。');
      setSaving(false);
      return;
    }

    setSelectedUserId(null);
    setNewRoleId(null);
    setAddModalOpen(false);
    setSaving(false);
    await fetchData();
  };

  // 新規ユーザーを Cognito に追加する
  const handleAddUser = async () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!addUserEmail || !emailRegex.test(addUserEmail)) {
      setAddUserEmailError('有効なメールアドレスを入力してください');
      return;
    }
    setAddUserLoading(true);
    setAddUserEmailError(null);
    try {
      const result = await api.post('/admin/users', { email: addUserEmail });
      setAddedUser(result);
      setAddUserModalOpen(false);
      setAddUserEmail('');
      await fetchData();
    } catch (e) {
      let errorMsg = 'ユーザーの追加に失敗しました';
      try {
        const parsed = JSON.parse(e.message);
        errorMsg = parsed.error || errorMsg;
      } catch {
        errorMsg = e.message || errorMsg;
      }
      setAddUserEmailError(errorMsg);
    } finally {
      setAddUserLoading(false);
    }
  };

  // ロール名に応じたバッジカラーを返す
  const roleColor = (name) => {
    if (name === 'admin') return 'red';
    if (name === 'editor') return 'blue';
    return 'gray';
  };

  if (loading) {
    return (
      <Center h={200}>
        <Loader size="sm" />
      </Center>
    );
  }

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Title order={3}>ユーザー管理</Title>
        <Group gap="xs">
          <Text size="sm" c="dimmed">合計: {users.length} 人</Text>
          <Button
            leftSection={<IconUserPlus size={16} />}
            size="sm"
            onClick={() => { setAddUserModalOpen(true); setAddUserEmail(''); setAddUserEmailError(null); }}
          >
            ユーザーを追加
          </Button>
          <Button
            leftSection={<IconPlus size={16} />}
            size="sm"
            onClick={() => setAddModalOpen(true)}
          >
            ロールを付与
          </Button>
        </Group>
      </Group>

      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
          {error}
        </Alert>
      )}

      {/* 全ユーザー一覧 */}
      <Table striped highlightOnHover withTableBorder>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>メールアドレス</Table.Th>
            <Table.Th>ユーザーID</Table.Th>
            <Table.Th>ロール</Table.Th>
            <Table.Th>資格ステータス</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {users.length === 0 ? (
            <Table.Tr>
              <Table.Td colSpan={4}>
                <Text c="dimmed" ta="center" size="sm">ユーザーが見つかりません</Text>
              </Table.Td>
            </Table.Tr>
          ) : (
            users.map((u) => (
              <Table.Tr key={u.id}>
                {/* メールアドレス + コピーボタン */}
                <Table.Td>
                  <Group gap={4} wrap="nowrap">
                    <Text size="sm">{u.email}</Text>
                    <CopyButton value={u.email ?? ''} timeout={1500}>
                      {({ copied, copy }) => (
                        <Tooltip label={copied ? 'コピー済み' : 'メールをコピー'} withArrow>
                          <ActionIcon
                            size="xs"
                            variant="subtle"
                            color={copied ? 'teal' : 'gray'}
                            onClick={copy}
                          >
                            {copied ? <IconCheck size={12} /> : <IconCopy size={12} />}
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </CopyButton>
                  </Group>
                </Table.Td>
                {/* ユーザーID + コピーボタン */}
                <Table.Td>
                  <Group gap={4} wrap="nowrap">
                    <Text size="xs" ff="monospace" c="dimmed">
                      {u.id.slice(0, 8)}…
                    </Text>
                    <CopyButton value={u.id} timeout={1500}>
                      {({ copied, copy }) => (
                        <Tooltip label={copied ? 'コピー済み' : 'IDをコピー'} withArrow>
                          <ActionIcon
                            size="xs"
                            variant="subtle"
                            color={copied ? 'teal' : 'gray'}
                            onClick={copy}
                          >
                            {copied ? <IconCheck size={12} /> : <IconCopy size={12} />}
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </CopyButton>
                  </Group>
                </Table.Td>
                {/* 付与済みロール */}
                <Table.Td>
                  <Group gap="xs">
                    {(userRoles[u.id] ?? []).map((r) => (
                      <Badge
                        key={r.id}
                        color={roleColor(r.name)}
                        variant="filled"
                        size="sm"
                        rightSection={
                          <IconTrash
                            size={12}
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleRemoveRole(u.id, r.id)}
                          />
                        }
                      >
                        {r.name}
                      </Badge>
                    ))}
                  </Group>
                </Table.Td>
                {/* 資格ステータス管理ボタン */}
                <Table.Td>
                  <Tooltip label="資格ステータスを管理" withArrow>
                    <ActionIcon
                      size="sm"
                      variant="subtle"
                      color="blue"
                      onClick={() => setCertStatusUser(u)}
                    >
                      <IconCertificate size={16} />
                    </ActionIcon>
                  </Tooltip>
                </Table.Td>
              </Table.Tr>
            ))
          )}
        </Table.Tbody>
      </Table>

      {/* 資格ステータス管理モーダル */}
      <UserCertificationStatus
        opened={!!certStatusUser}
        onClose={() => setCertStatusUser(null)}
        user={certStatusUser}
      />

      {/* ユーザー追加モーダル */}
      <Modal
        opened={addUserModalOpen}
        onClose={() => { setAddUserModalOpen(false); setAddUserEmail(''); setAddUserEmailError(null); }}
        title="ユーザーを追加"
      >
        <Stack gap="sm">
          <TextInput
            label="メールアドレス"
            placeholder="user@example.com"
            value={addUserEmail}
            onChange={(e) => { setAddUserEmail(e.currentTarget.value); setAddUserEmailError(null); }}
            error={addUserEmailError}
            required
          />
          <Group justify="flex-end" mt="xs">
            <Button variant="default" onClick={() => { setAddUserModalOpen(false); setAddUserEmail(''); setAddUserEmailError(null); }}>キャンセル</Button>
            <Button onClick={handleAddUser} loading={addUserLoading}>追加する</Button>
          </Group>
        </Stack>
      </Modal>

      {/* ユーザー追加完了モーダル（生成パスワード表示） */}
      <Modal
        opened={!!addedUser}
        onClose={() => setAddedUser(null)}
        title="ユーザーを追加しました"
      >
        <Stack gap="sm">
          <Text size="sm">以下の情報を利用者に安全な方法で連絡してください。</Text>
          <TextInput label="メールアドレス" value={addedUser?.email ?? ''} readOnly />
          <Group gap="xs" align="flex-end">
            <TextInput
              label="初期パスワード"
              value={addedUser?.temporaryPassword ?? ''}
              readOnly
              style={{ flex: 1 }}
            />
            <CopyButton value={addedUser?.temporaryPassword ?? ''} timeout={1500}>
              {({ copied, copy }) => (
                <Tooltip label={copied ? 'コピー済み' : 'コピー'} withArrow>
                  <ActionIcon
                    size="lg"
                    variant="light"
                    color={copied ? 'teal' : 'blue'}
                    onClick={copy}
                    mb={1}
                  >
                    {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                  </ActionIcon>
                </Tooltip>
              )}
            </CopyButton>
          </Group>
          <Alert icon={<IconAlertCircle size={16} />} color="yellow" variant="light" size="sm">
            このパスワードはこの画面にのみ表示されます。必ず利用者に連絡してください。
          </Alert>
          <Group justify="flex-end" mt="xs">
            <Button onClick={() => setAddedUser(null)}>閉じる</Button>
          </Group>
        </Stack>
      </Modal>

      {/* ロール付与モーダル */}
      <Modal
        opened={addModalOpen}
        onClose={() => { setAddModalOpen(false); setSelectedUserId(null); setNewRoleId(null); }}
        title="ロールを付与する"
      >
        <Stack gap="sm">
          {/* ユーザーをメールアドレスで選択する */}
          <Select
            label="ユーザー"
            placeholder="メールアドレスで検索"
            searchable
            data={users.map((u) => ({ value: u.id, label: u.email ?? u.id }))}
            value={selectedUserId}
            onChange={setSelectedUserId}
          />
          <Select
            label="付与するロール"
            placeholder="ロールを選択"
            data={roles.map((r) => ({ value: r.id, label: r.name }))}
            value={newRoleId}
            onChange={setNewRoleId}
          />
          {error && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light" size="sm">
              {error}
            </Alert>
          )}
          <Group justify="flex-end" mt="xs">
            <Button variant="default" onClick={() => setAddModalOpen(false)}>キャンセル</Button>
            <Button
              onClick={handleAddRole}
              loading={saving}
              disabled={!selectedUserId || !newRoleId}
            >
              付与する
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}

export default UserManagement;
